import { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarIcon, Loader2 } from 'lucide-react';
import { format } from 'date-fns';

import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { UserAvatar } from '@/components/shared/UserAvatar';
import { useToast } from '@/hooks/use-toast';
import { sprintService } from '@/services/sprint.service';
import { groupService } from '@/services/group.service';
import { cn } from '@/lib/utils';

const SPRINT_STAGES = ['Business', 'Development', 'Testing', 'UAT'] as const;

interface CreateSprintDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
  defaultGroupId?: string;
}

export function CreateSprintDialog({ open, onOpenChange, onSuccess, defaultGroupId }: CreateSprintDialogProps) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [groupId, setGroupId] = useState(defaultGroupId || '');
  const [startDate, setStartDate] = useState<Date | undefined>();
  const [endDate, setEndDate] = useState<Date | undefined>();
  const [leaderIds, setLeaderIds] = useState<Record<string, string[]>>({
    business: [],
    development: [],
    testing: [],
    uat: [],
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Fetch groups
  const { data: groups } = useQuery({
    queryKey: ['groups-for-select'],
    queryFn: () => groupService.list({ limit: 100 }).then((r) => r.data.data),
    enabled: open,
  });

  // Fetch group members when group changes
  const { data: groupMembers } = useQuery({
    queryKey: ['group-members', groupId],
    queryFn: () => groupService.listMembers(groupId).then((r) => r.data.data),
    enabled: !!groupId && open,
  });

  const members = groupMembers ?? [];

  useEffect(() => {
    if (defaultGroupId && !groupId) setGroupId(defaultGroupId);
  }, [defaultGroupId]);

  function resetForm() {
    setName(''); setDescription(''); setGroupId(defaultGroupId || '');
    setStartDate(undefined); setEndDate(undefined);
    setLeaderIds({ business: [], development: [], testing: [], uat: [] });
    setErrors({});
  }

  function validate(): boolean {
    const errs: Record<string, string> = {};
    if (!name.trim()) errs.name = 'Name is required';
    if (!groupId) errs.group = 'Group is required';
    if (!startDate) errs.start = 'Start date is required';
    if (!endDate) errs.end = 'End date is required';
    if (startDate && endDate && endDate < startDate) errs.end = 'End date must be after start date';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  function toggleLeader(stage: string, userId: string) {
    setLeaderIds((prev) => {
      const current = prev[stage] || [];
      return {
        ...prev,
        [stage]: current.includes(userId)
          ? current.filter((id) => id !== userId)
          : [...current, userId],
      };
    });
  }

  const createMut = useMutation({
    mutationFn: () => {
      // Build column_leaders array
      const column_leaders: Array<{ column_name: string; leader_id: string }> = [];
      for (const stage of SPRINT_STAGES) {
        const ids = leaderIds[stage.toLowerCase()] || [];
        for (const lid of ids) {
          column_leaders.push({ column_name: stage, leader_id: lid });
        }
      }

      return sprintService.create({
        name,
        description: description || undefined,
        group_id: groupId,
        start_date: format(startDate!, 'yyyy-MM-dd'),
        end_date: format(endDate!, 'yyyy-MM-dd'),
        column_leaders: column_leaders.length > 0 ? column_leaders : undefined,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['work-sprints'] });
      toast({ title: 'Sprint created' });
      resetForm();
      onOpenChange(false);
      onSuccess?.();
    },
    onError: (e: any) => toast({ title: 'Error', description: e.response?.data?.error?.message || 'Failed to create sprint', variant: 'destructive' }),
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (validate()) createMut.mutate();
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) resetForm(); onOpenChange(v); }}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create New Sprint</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Sprint Name *</Label>
            <Input placeholder="Enter sprint name" value={name} onChange={(e) => setName(e.target.value)} />
            {errors.name && <p className="text-xs text-destructive">{errors.name}</p>}
          </div>

          <div className="space-y-2">
            <Label>Description</Label>
            <textarea
              className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring resize-y"
              placeholder="Enter description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label>Group *</Label>
            <Select value={groupId} onValueChange={setGroupId}>
              <SelectTrigger><SelectValue placeholder="Select a group" /></SelectTrigger>
              <SelectContent>
                {(groups ?? []).map((g: any) => (
                  <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.group && <p className="text-xs text-destructive">{errors.group}</p>}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Start Date *</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className={cn('w-full pl-3 text-left font-normal', !startDate && 'text-muted-foreground')}>
                    {startDate ? format(startDate, 'PPP') : 'Pick a date'}
                    <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar mode="single" selected={startDate} onSelect={setStartDate} initialFocus />
                </PopoverContent>
              </Popover>
              {errors.start && <p className="text-xs text-destructive">{errors.start}</p>}
            </div>
            <div className="space-y-2">
              <Label>End Date *</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className={cn('w-full pl-3 text-left font-normal', !endDate && 'text-muted-foreground')}>
                    {endDate ? format(endDate, 'PPP') : 'Pick a date'}
                    <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar mode="single" selected={endDate} onSelect={setEndDate} initialFocus />
                </PopoverContent>
              </Popover>
              {errors.end && <p className="text-xs text-destructive">{errors.end}</p>}
            </div>
          </div>

          {groupId && members.length > 0 && (
            <div className="space-y-4 pt-4 border-t">
              <div>
                <Label className="text-base font-semibold">Stage Leaders</Label>
                <p className="text-sm text-muted-foreground mt-1">
                  Assign one or more leaders for each development stage. Leaders can manage tasks in their column.
                </p>
              </div>

              <div className="space-y-4">
                {SPRINT_STAGES.map((stage) => {
                  const key = stage.toLowerCase();
                  const selected = leaderIds[key] || [];

                  return (
                    <div key={stage} className="space-y-2">
                      <Label>{stage} Leaders</Label>
                      <div className="border rounded-md p-3 space-y-1 max-h-36 overflow-y-auto">
                        {members.map((member: any) => {
                          const uid = member.user?.id || member.user_id;
                          const name = member.user?.full_name || 'Unknown';
                          const avatar = member.user?.avatar_url;
                          const isSelected = selected.includes(uid);
                          return (
                            <label
                              key={uid}
                              className={cn(
                                'flex items-center gap-3 p-2 rounded-md cursor-pointer hover:bg-muted/50 transition-colors',
                                isSelected && 'bg-primary/10'
                              )}
                            >
                              <Checkbox checked={isSelected} onCheckedChange={() => toggleLeader(key, uid)} />
                              <UserAvatar name={name} avatarUrl={avatar} size="xs" />
                              <span className="text-sm">{name}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" className="bg-brand-primary hover:bg-brand-primary-dark" disabled={createMut.isPending}>
              {createMut.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Create Sprint
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
